import { englishConversationInstructions } from "../../shared/conversation-language.ts";
import { endpointFor } from "@delegate/shared/tools";
import {
  DELEGATE_TOOL_NAME,
  type DebugEvent,
  type DebugEventType,
  type DelegateResult,
  type Status,
} from "@delegate/shared";
import {
  RealtimeFunctionCallLoop,
  type RealtimeTransport,
} from "@delegate/shared/realtime-loop";
import { getAvatarId } from "./api.ts";
import { createEchoGuard, microphoneEnabled, type EchoGuard } from "./echo-guard.ts";
import { TOOL_ACTION_NAMES } from "../../shared/tools.ts";
import { FEEDBACK_ACTION_NAMES } from "../../shared/feedback.ts";
import {
  REPRESENTATIVE_TOOL_NAMES,
  type RepresentativeActionResult,
} from "../../shared/representative.ts";

/**
 * How many times the startup briefing check may be asked for.
 *
 * Greeting the avatar immediately cancels the first attempt, so one retry is
 * the common path. After this it is abandoned quietly - the check is
 * instrumentation, and it must never take the turn away from the user.
 */
const BRIEFING_MAX_ATTEMPTS = 3;

export type TranscriptEntry = {
  role: "user" | "assistant";
  text: string;
  timestamp: string;
};

export type CharterSection = { id: string; title: string; body: string };
export type CharterView = {
  sections: CharterSection[];
  tools: { id: string; summary: string; usage?: string; docsUrl?: string }[];
};

export type RealtimeCallbacks = {
  onStatus: (s: Status) => void;
  onCharter?: (c: CharterView) => void;
  /** undefined while pending, then the model's own words, or null if it failed. */
  onBriefingAck?: (ack: string | null) => void;
  onCost?: (sessionUsd: number) => void;
  onDebug: (e: DebugEvent) => void;
  onTranscript: (t: TranscriptEntry) => void;
  onError: (message: string) => void;
  onAudioStream?: (stream: MediaStream | null) => void;
  onSurfaceResult?: (
    result: RepresentativeActionResult,
  ) => RepresentativeActionResult | void;
  beforeTool?: (name: string, args: unknown) => DelegateResult | undefined;
  onRealtimeEvent?: (event: Record<string, unknown>) => void;
};

const OPENAI_CALLS_URL = "https://api.openai.com/v1/realtime/calls";

export class RealtimeSessionClient {
  private readonly avatarId = getAvatarId();
  private readonly abort = new AbortController();
  private stopped = false;
  private sharedCallId?: string;
  private sharedConversation = false;
  private callStartedAt = new Date().toISOString();
  private callConnectedAt = 0;
  private durationTimer?: ReturnType<typeof setTimeout>;
  private hasTurns = false;
  private headers(): Record<string, string> {
    return this.avatarId ? { "x-avatar-id": this.avatarId } : {};
  }
  private assertActive() {
    if (getAvatarId() !== this.avatarId) this.stop();
    if (this.stopped) throw new Error("This conversation has ended.");
  }
  private pc?: RTCPeerConnection;
  private dc?: RTCDataChannel;
  private mic?: MediaStream;
  private audioEl?: HTMLAudioElement;
  private loop?: RealtimeFunctionCallLoop;
  private openaiSessionId?: string;
  private audioAnnouncedFor = new Set<string>();
  private awaitingBriefingAck = false;
  /** Which response carries the briefing answer, so an interruption is attributable. */
  private briefingResponseId: string | null = null;
  private briefingAttempts = 0;
  private briefingTimer: ReturnType<typeof setTimeout> | null = null;
  private userIsSpeaking = false;
  /** Keeps the avatar from answering its own voice leaking back through the speakers. */
  private echoGuard: EchoGuard = createEchoGuard();
  private hostMuted = false;
  /** Only the granted track's settings prove the browser honoured echo cancellation. */
  private echoCancellationConfirmed = false;
  /** A response already in flight; starting another is an API error. */
  private activeResponseId: string | null = null;
  private connectStartedAt = 0;

  readonly localSessionId: string;
  /** Reported to the server so date/time questions need no tool call. */
  readonly timezone: string =
    Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

  constructor(
    private readonly cb: RealtimeCallbacks,
    localSessionId?: string,
  ) {
    this.localSessionId = localSessionId ?? `local_${crypto.randomUUID()}`;
  }

  private transcript(entry: TranscriptEntry) {
    this.cb.onTranscript(entry);
    if (this.sharedCallId && this.avatarId)
      void fetch(
        `/api/communications/employees/${encodeURIComponent(this.avatarId)}/turns`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: crypto.randomUUID(),
            callId: this.sharedCallId,
            sender: entry.role === "user" ? "user" : "employee",
            text: entry.text,
          }),
        },
      )
        .then((r) => {
          if (!r.ok) throw Error("Transcript sync failed");
        })
        .catch(() => {
          if (!this.stopped)
            this.cb.onError(
              "The call continues, but this transcript could not sync.",
            );
        });
  }

  private emit(
    eventType: DebugEventType,
    extra: Partial<
      Omit<DebugEvent, "timestamp" | "localSessionId" | "eventType">
    > = {},
  ) {
    const event: DebugEvent = {
      timestamp: new Date().toISOString(),
      localSessionId: this.localSessionId,
      openaiSessionId: this.openaiSessionId,
      eventType,
      ...extra,
    };
    this.cb.onDebug(event);
    if (this.sharedConversation) return;
    // Mirror into server logs so the whole loop is visible in `railway logs`.
    void fetch("/api/log", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...this.headers() },
      body: JSON.stringify(event),
    }).catch(() => {});
  }

  /* --------------------------- lifecycle --------------------------- */

  async start(
    options: {
      microphone?: boolean;
      greeting?: string;
      sharedConversation?: boolean;
    } = {},
  ) {
    this.sharedConversation = !!options.sharedConversation;
    this.assertActive();
    this.connectStartedAt = performance.now();
    this.cb.onStatus("CONNECTING");
    this.emit("SESSION_STARTING");

    // Request permission before minting a paid session. Text can connect without a microphone.
    if (options.microphone !== false) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        });
        if (this.stopped) {
          stream.getTracks().forEach((track) => track.stop());
          this.assertActive();
        }
        this.mic = stream;
        this.readMicrophoneSettings(stream);
      } catch {
        this.assertActive();
        throw new Error(
          "Microphone access is unavailable. Enable it to speak, or continue with text.",
        );
      }
    }
    this.assertActive();
    // Ephemeral credential from OUR server. The real API key stays there.
    const tokenRes = await fetch(
      options.sharedConversation
        ? "/api/communications/realtime/session"
        : "/api/session",
      {
        method: "POST",
        signal: this.abort.signal,
        headers: { "Content-Type": "application/json", ...this.headers() },
        body: JSON.stringify({
          localSessionId: this.localSessionId,
          timezone: this.timezone,
          ...(options.sharedConversation ? { employeeId: this.avatarId } : {}),
        }),
      },
    );
    if (!tokenRes.ok) {
      if (options.sharedConversation && tokenRes.status === 429) {
        throw new Error(
          "The voice-call allowance for this workspace has been reached. You can continue with text and try calling again later.",
        );
      }
      throw new Error("Could not connect the conversation. Please try again.");
    }
    const session = (await tokenRes.json()) as {
      clientSecret: string;
      value?: string;
      callId?: string;
      maxDurationSeconds?: number;
      charter?: CharterView;
    };
    const clientSecret = session.value ?? session.clientSecret;
    this.sharedCallId = session.callId;
    if (options.sharedConversation)
      this.durationTimer = setTimeout(
        () => this.stop(),
        Math.min(180, session.maxDurationSeconds ?? 180) * 1000,
      );
    this.assertActive();
    if (session.charter) this.cb.onCharter?.(session.charter);

    // 3. Peer connection + remote audio playback.
    const pc = new RTCPeerConnection();
    this.pc = pc;

    const audioEl = document.createElement("audio");
    audioEl.autoplay = true;
    this.audioEl = audioEl;
    pc.ontrack = (e) => {
      if (this.stopped) return;
      audioEl.srcObject = e.streams[0];
      this.cb.onAudioStream?.(e.streams[0]);
      void audioEl.play().catch(() => {
        if (!this.stopped)
          this.cb.onError(
            "Your browser paused audio. Choose Enable audio to hear the conversation.",
          );
      });
    };
    if (this.mic) pc.addTrack(this.mic.getTracks()[0], this.mic);
    else pc.addTransceiver("audio", { direction: "recvonly" });

    pc.onconnectionstatechange = () => {
      if (
        !this.stopped &&
        (pc.connectionState === "failed" ||
          pc.connectionState === "disconnected")
      ) {
        this.stop();
        this.cb.onError(`WebRTC connection ${pc.connectionState}`);
        this.cb.onStatus("ERROR");
        this.emit("ERROR", { payloadSummary: `webrtc ${pc.connectionState}` });
      }
    };

    // 4. Event data channel.
    const dc = pc.createDataChannel("oai-events");
    this.dc = dc;

    const transport: RealtimeTransport = {
      send: (event) => {
        if (getAvatarId() !== this.avatarId) {
          this.stop();
          return;
        }
        if (!this.stopped && dc.readyState === "open")
          dc.send(JSON.stringify(event));
      },
    };

    this.loop = new RealtimeFunctionCallLoop({
      transport,
      timeoutMs: ({ args }) => {
        const a = args as any;
        if (a?.reason !== "web_lookup") return 12_000;
        // A deep lookup is allowed the time it was granted; a quick one is not.
        return a?.depth === "deep" ? 70_000 : 20_000;
      },
      execute: async ({ callId, name, args }) =>
        this.executeTool(callId, name, args),
      onDebug: (e) => this.emit(e.eventType, e),
    });

    dc.addEventListener("open", () => {
      if (this.stopped) return;
      const ms = Math.round(performance.now() - this.connectStartedAt);
      this.callConnectedAt = Date.now();
      this.cb.onStatus("LISTENING");
      this.emit("SESSION_CONNECTED", { durationMs: ms });
      if (options.greeting)
        dc.send(
          JSON.stringify({
            type: "response.create",
            response: { instructions: englishConversationInstructions(options.greeting) },
          }),
        );
      else if (!options.sharedConversation) this.requestBriefingAck();
    });
    dc.addEventListener("message", (e) => {
      if (!this.stopped) this.handleServerEvent(e.data);
    });

    // 5. SDP exchange - https://platform.openai.com/docs/api-reference/realtime
    const offer = await pc.createOffer();
    this.assertActive();
    await pc.setLocalDescription(offer);
    const sdpRes = await fetch(OPENAI_CALLS_URL, {
      method: "POST",
      signal: this.abort.signal,
      body: offer.sdp,
      headers: {
        Authorization: `Bearer ${clientSecret}`,
        "Content-Type": "application/sdp",
      },
    });
    if (!sdpRes.ok) {
      throw new Error(
        "Could not establish the conversation. Please try again.",
      );
    }
    const answer = await sdpRes.text();
    this.assertActive();
    await pc.setRemoteDescription({ type: "answer", sdp: answer });
    // WebRTC + data channel events: https://developers.openai.com/api/docs/guides/realtime-webrtc
    if (dc.readyState !== "open")
      await new Promise<void>((resolve, reject) => {
        const finish = (error?: Error) => {
          clearTimeout(timer);
          dc.removeEventListener("open", opened);
          this.abort.signal.removeEventListener("abort", aborted);
          error ? reject(error) : resolve();
        };
        const opened = () => finish();
        const aborted = () => finish(new Error("This conversation has ended."));
        const timer = setTimeout(
          () =>
            finish(
              new Error(
                "The conversation took too long to connect. Please try again.",
              ),
            ),
          20_000,
        );
        dc.addEventListener("open", opened, { once: true });
        this.abort.signal.addEventListener("abort", aborted, { once: true });
        if (this.stopped) aborted();
      });
    this.assertActive();
  }

  setMuted(muted: boolean) {
    this.hostMuted = muted;
    this.applyMicrophoneState();
  }

  private readMicrophoneSettings(stream: MediaStream) {
    const track = (stream.getAudioTracks?.() ?? stream.getTracks())[0];
    const settings = (track?.getSettings?.() ?? {}) as MediaTrackSettings;
    this.echoCancellationConfirmed = settings.echoCancellation === true;
    this.echoGuard = createEchoGuard();
    this.hostMuted = false;
    if (!this.echoCancellationConfirmed) {
      // "RAW" is the shared debug type for diagnostics without their own event.
      this.emit("RAW", {
        payloadSummary: "ECHO_CANCELLATION_UNCONFIRMED " + JSON.stringify({
          echoCancellation: settings.echoCancellation ?? null,
          noiseSuppression: settings.noiseSuppression ?? null,
          autoGainControl: settings.autoGainControl ?? null,
        }),
      });
    }
  }

  /**
   * With confirmed echo cancellation the call stays full duplex. Without it
   * the microphone closes while the avatar is audibly speaking, so its own
   * voice cannot interrupt it; a host mute always wins.
   */
  private applyMicrophoneState() {
    const enabled = microphoneEnabled({
      hostMuted: this.hostMuted,
      assistantSpeaking: this.echoGuard.assistantSpeaking,
      echoCancellationConfirmed: this.echoCancellationConfirmed,
    });
    (this.mic?.getAudioTracks?.() ?? this.mic?.getTracks() ?? []).forEach((track) => {
      track.enabled = enabled;
    });
  }
  async resumeAudio() {
    await this.audioEl?.play();
  }

  stop() {
    clearTimeout(this.durationTimer);
    if (this.stopped) return;
    this.stopped = true;
    this.abort.abort();
    // A pending retry must not fire into a closed session.
    this.awaitingBriefingAck = false;
    this.clearBriefingTimer();
    try {
      this.dc?.close();
      this.pc?.getSenders().forEach((s) => s.track?.stop());
      this.pc?.close();
      this.mic?.getTracks().forEach((t) => t.stop());
      if (this.audioEl) {
        this.audioEl.pause();
        this.audioEl.srcObject = null;
      }
      this.cb.onAudioStream?.(null);
    } catch {
      /* stopping must never throw */
    }
    this.pc = undefined;
    this.dc = undefined;
    this.mic = undefined;
    this.emit("SESSION_STOPPED");
    this.cb.onStatus("DISCONNECTED");

    if (this.sharedCallId)
      void fetch("/api/communications/calls", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: this.sharedCallId,
          employeeId: this.avatarId,
          kind: "audio",
          startedAt: this.callStartedAt,
          durationSeconds: this.callConnectedAt
            ? Math.floor((Date.now() - this.callConnectedAt) / 1000)
            : 0,
          outcome: this.callConnectedAt ? "completed" : "cancelled",
        }),
      }).catch(() => {});
    if (this.sharedConversation || !this.hasTurns) return;

    // Out of band: turn what was just said into topics and observations. The
    // session is already closed, so this costs the user nothing.
    void fetch("/api/summarize", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...this.headers() },
      body: JSON.stringify({ localSessionId: this.localSessionId }),
    })
      .then((r) => r.json())
      .then((d: any) =>
        this.emit("SESSION_SUMMARIZED", {
          payloadSummary: {
            topics: d?.topicsTouched,
            observations: d?.observationsRecorded,
            promoted: d?.promoted,
          },
        }),
      )
      .catch(() => {});
  }

  /**
   * Ask the model to state the briefing it received, in text rather than audio.
   *
   * Without this, "the model has the charter" is an assumption. The reply is
   * the model's own words, so a briefing that failed to apply shows up as a
   * wrong or missing answer instead of silently behaving oddly later.
   *
   * The exchange is deliberately kept out of the transcript and out of stored
   * turns: it is instrumentation, not conversation, and recording it would put
   * it into memory and summarisation.
   */
  private clearBriefingTimer() {
    if (this.briefingTimer) clearTimeout(this.briefingTimer);
    this.briefingTimer = null;
  }

  private requestBriefingAck() {
    if (!this.dc || this.dc.readyState !== "open") return;
    // Never compete with a response that is already running: the API rejects a
    // second one outright, and the turn that matters is the user's.
    if (this.activeResponseId || this.userIsSpeaking) {
      this.awaitingBriefingAck = true;
      return this.retryBriefingAck();
    }
    this.awaitingBriefingAck = true;
    this.briefingAttempts++;
    this.briefingResponseId = null;
    this.dc.send(
      JSON.stringify({
        type: "conversation.item.create",
        item: {
          type: "message",
          role: "user",
          content: [
            {
              type: "input_text",
              // Asking the model to copy text out of its own instructions reads
              // as an extraction attempt, and it refuses - "I can't provide
              // that" was being displayed as though it were an acknowledgement.
              // Asking it to confirm what it understood gets the same signal.
              text:
                "Readiness check - reply with one line of text and do not speak. " +
                "Give your name, your job in a few words, and how many tools you can use. " +
                "Format: NAME | JOB | N tools",
            },
          ],
        },
      }),
    );
    this.dc.send(
      JSON.stringify({
        type: "response.create",
        response: { output_modalities: ["text"] },
      }),
    );

    // A model that never answers must not leave the panel pending forever.
    // Interruption is handled separately, in response.done: greeting the avatar
    // the moment the session opens cancels this response, which is the user
    // behaving normally, not a failure.
    if (this.briefingTimer) clearTimeout(this.briefingTimer);
    this.briefingTimer = setTimeout(() => {
      if (!this.awaitingBriefingAck) return;
      this.awaitingBriefingAck = false;
      this.cb.onBriefingAck?.(null);
      this.emit("ERROR", {
        payloadSummary: "briefing not acknowledged within 15s",
      });
    }, 15_000);
  }

  /**
   * Ask again after the user's own turn cancelled the check.
   *
   * Speaking first is the common case - the session opens, the user says hello,
   * and semantic VAD cancels whatever was in flight. Retrying keeps the panel
   * honest without ever competing with the person for the turn.
   */
  private retryBriefingAck() {
    if (this.briefingAttempts >= BRIEFING_MAX_ATTEMPTS) {
      this.awaitingBriefingAck = false;
      this.cb.onBriefingAck?.(null);
      this.emit("BRIEFING_SKIPPED", {
        payloadSummary:
          "the user was speaking, so the briefing check was not run",
      });
      return;
    }
    if (this.briefingTimer) clearTimeout(this.briefingTimer);
    this.briefingTimer = setTimeout(() => {
      if (!this.awaitingBriefingAck) return;
      if (this.userIsSpeaking) return this.retryBriefingAck(); // still talking; wait again
      this.requestBriefingAck();
    }, 1_500);
  }

  /** Text input path - same machinery as speech, useful for debugging without a mic. */
  sendText(text: string) {
    this.assertActive();
    if (!this.dc || this.dc.readyState !== "open") return;
    this.dc.send(
      JSON.stringify({
        type: "conversation.item.create",
        item: {
          type: "message",
          role: "user",
          content: [{ type: "input_text", text }],
        },
      }),
    );
    this.dc.send(JSON.stringify({ type: "response.create" }));
    this.recordTurn("user", text);
    this.transcript({
      role: "user",
      text,
      timestamp: new Date().toISOString(),
    });
    this.emit("USER_TURN", { payloadSummary: text });
  }

  /* ---------------------------- internals -------------------------- */

  private async executeTool(
    callId: string,
    name: string,
    args: unknown,
  ): Promise<DelegateResult> {
    this.assertActive();
    const blocked = this.cb.beforeTool?.(name, args);
    if (blocked) return blocked;
    this.cb.onStatus("TOOL_RUNNING");
    const representative = (
      REPRESENTATIVE_TOOL_NAMES as readonly string[]
    ).includes(name);
    // Registered tool actions (tasks, calendar) run through the tools executor; the
    // representative's own set, notes included, keeps its routes.
    const registered =
      !representative &&
      (TOOL_ACTION_NAMES.includes(name) ||
        FEEDBACK_ACTION_NAMES.includes(name));
    const surfaced = representative || registered;
    const endpoint = representative
      ? this.sharedConversation
        ? `/api/communications/employees/${encodeURIComponent(this.avatarId!)}/note-actions`
        : "/api/representative/actions"
      : registered
        ? this.sharedConversation
          ? `/api/communications/employees/${encodeURIComponent(this.avatarId!)}/tool-actions`
          : "/api/tools/actions"
        : endpointFor(name);

    const res = await fetch(endpoint, {
      method: "POST",
      signal: this.abort.signal,
      headers: { "Content-Type": "application/json", ...this.headers() },
      body: JSON.stringify({
        ...(this.sharedConversation
          ? { voiceSessionId: this.sharedCallId }
          : {}),
        localSessionId: this.localSessionId,
        callId,
        name,
        args,
        timezone: this.timezone,
      }),
    });
    let payload = await res.json();
    if (this.sharedConversation && surfaced && res.ok)
      payload = { result: payload };
    if (!res.ok && !(surfaced && payload?.result?.success === false))
      throw new Error(`Delegate backend returned ${res.status}`);
    let { result, instructions } = payload as {
      result: DelegateResult;
      instructions?: string;
    };
    this.assertActive();
    if (surfaced)
      result =
        this.cb.onSurfaceResult?.(result as RepresentativeActionResult) ??
        result;

    // A preference the user just stated has to bind to THIS session, not the next
    // one. session.update accepts new instructions mid-session, so push them now.
    if (instructions && this.dc?.readyState === "open") {
      this.dc.send(
        JSON.stringify({
          type: "session.update",
          session: { type: "realtime", instructions },
        }),
      );
      this.emit("PREFERENCE_APPLIED", {
        toolCallId: callId,
        payloadSummary: args,
      });
    }
    return result;
  }

  private reportUsage(usage: unknown) {
    void fetch("/api/usage", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...this.headers() },
      body: JSON.stringify({ localSessionId: this.localSessionId, usage }),
    })
      .then((r) => r.json())
      .then((d: any) => {
        if (typeof d?.session?.totalUsd === "number")
          this.cb.onCost?.(d.session.totalUsd);
        if (typeof d?.cost?.totalUsd === "number") {
          this.emit("COST", {
            payloadSummary: {
              turnUsd: Number(d.cost.totalUsd.toFixed(5)),
              audioUsd: Number(d.cost.audioUsd.toFixed(5)),
              inTokens: d.cost.inputTokens,
              outTokens: d.cost.outputTokens,
              audioTokens: d.cost.audioTokens,
              cached: d.cost.cachedTokens,
            },
          });
        }
      })
      .catch(() => {});
  }

  private recordTurn(role: "user" | "assistant", text: string) {
    if (this.sharedConversation) return;
    if (this.stopped) return;
    this.hasTurns = true;
    void fetch("/api/turn", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...this.headers() },
      body: JSON.stringify({ localSessionId: this.localSessionId, role, text }),
    }).catch(() => {});
  }

  /** Realtime server events - https://platform.openai.com/docs/api-reference/realtime */
  private handleServerEvent(raw: string) {
    let event: any;
    try {
      event = JSON.parse(raw);
    } catch {
      return;
    }

    // Cancelling a self-reply can race its natural end; that is not a failure.
    if (event.type === "error" && event.error?.code === "response_cancel_not_active") return;
    const echo = this.echoGuard.handle(event, performance.now());
    if (
      event.type === "output_audio_buffer.started" ||
      event.type === "output_audio_buffer.stopped" ||
      event.type === "output_audio_buffer.cleared"
    )
      this.applyMicrophoneState();
    if (echo.cancelResponse && this.dc?.readyState === "open")
      this.dc.send(JSON.stringify({ type: "response.cancel" }));

    this.cb.onRealtimeEvent?.(event);

    switch (event.type) {
      case "session.created":
      case "session.updated":
        this.openaiSessionId = event.session?.id ?? this.openaiSessionId;
        break;

      case "input_audio_buffer.speech_started":
        this.userIsSpeaking = true;
        this.emit("SPEECH_STARTED");
        this.cb.onStatus("LISTENING");
        break;

      case "input_audio_buffer.speech_stopped":
        this.userIsSpeaking = false;
        this.emit("SPEECH_STOPPED");
        break;

      case "conversation.item.input_audio_transcription.completed": {
        const text = String(event.transcript ?? "").trim();
        if (echo.ignoreTranscript) {
          // The avatar's own words came back through the microphone.
          this.emit("RAW", { payloadSummary: `ECHO_IGNORED ${text.slice(0, 200)}` });
          break;
        }
        if (text) {
          this.transcript({
            role: "user",
            text,
            timestamp: new Date().toISOString(),
          });
          this.recordTurn("user", text);
          this.emit("USER_TURN", { payloadSummary: text });
        }
        break;
      }

      case "response.created":
        this.activeResponseId = String(event.response?.id ?? "");
        if (this.awaitingBriefingAck && !this.briefingResponseId) {
          this.briefingResponseId = this.activeResponseId;
        }
        this.emit("RESPONSE_STARTED", { payloadSummary: event.response?.id });
        break;

      case "response.output_audio.delta":
      case "response.audio.delta": {
        const id = String(event.response_id ?? "unknown");
        if (!this.audioAnnouncedFor.has(id)) {
          this.audioAnnouncedFor.add(id);
          this.emit("AUDIO_PLAYING", { payloadSummary: id });
          this.cb.onStatus("SPEAKING");
        }
        break;
      }

      case "response.output_text.done":
      case "response.text.done": {
        if (this.awaitingBriefingAck) {
          this.awaitingBriefingAck = false;
          const ack = String(event.text ?? "").trim();
          this.cb.onBriefingAck?.(ack || null);
          this.emit("BRIEFING_ACKNOWLEDGED", {
            payloadSummary: ack.slice(0, 200),
          });
        }
        break;
      }

      case "response.output_audio_transcript.done":
      case "response.audio_transcript.done": {
        const text = String(event.transcript ?? "").trim();
        if (text) {
          this.transcript({
            role: "assistant",
            text,
            timestamp: new Date().toISOString(),
          });
          this.recordTurn("assistant", text);
          this.emit("ASSISTANT_TURN", { payloadSummary: text });
        }
        break;
      }

      case "response.done": {
        if (this.activeResponseId === String(event.response?.id ?? ""))
          this.activeResponseId = null;
        // Token usage arrives per response; this is the only place the browser
        // can see what the voice turn actually cost.
        if (event.response?.usage) this.reportUsage(event.response.usage);
        const status = event.response?.status;
        if (
          this.awaitingBriefingAck &&
          this.briefingResponseId &&
          String(event.response?.id ?? "") === this.briefingResponseId &&
          status !== "completed"
        ) {
          // Cancelled or incomplete: the user started talking over it.
          this.retryBriefingAck();
          break;
        }
        if (status === "failed") {
          const msg = JSON.stringify(event.response?.status_details ?? {});
          this.cb.onError(`Realtime response failed: ${msg}`);
          this.emit("ERROR", { payloadSummary: msg });
        }
        break;
      }

      case "error": {
        const msg =
          event.error?.message ?? JSON.stringify(event.error ?? event);
        this.cb.onError(msg);
        this.emit("ERROR", { payloadSummary: msg });
        break;
      }

      default:
        break;
    }

    // Tool calls drive the Delegate loop.
    const pending = this.loop?.handleEvent(event) ?? [];
    if (pending.length > 0) {
      void Promise.allSettled(pending).then(() => {
        if (this.pc) this.cb.onStatus("SPEAKING");
      });
    } else if (
      event.type === "response.done" &&
      this.loop?.pendingCount === 0 &&
      this.pc
    ) {
      this.cb.onStatus("LISTENING");
    }
  }
}

export const TOOL_NAME = DELEGATE_TOOL_NAME;
